import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import RefreshIcon from '@mui/icons-material/Refresh';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid2';
import InputLabel from '@mui/material/InputLabel';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { find } from 'lodash';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { object, string, mixed, boolean } from 'yup';

import {
  BACKUP_LABELS,
  PERIOD_TYPES,
  BACKUP_PERIOD_TYPE_LABELS,
  BACKUP_TYPES,
  DEFAULT_EXCLUDE_FILES,
  DEFAULT_STANDARD_INCLUDE_FILES,
  DEFAULT_BACKUP_TIME,
  DAYS,
  DEFAULT_TIMEZONE,
  INSTANCE_CATEGORY,
  DEFAULT_COMMUNITY_INCLUDE_FILES,
} from '../../../constants';
import { useUser } from '../../../contexts/UserContext';
import { isSolrCloud, sentenceCase } from '../../../utils/utils';
import ActionButton from '../../elements/action-button/ActionButton';
import Loading from '../../elements/loading/Loading';
import Text from '../../elements/text/Text';
import Modal from '../../sections/modal/Modal';

const ScheduledBackupForm = ({
  data,
  handleCreate,
  handleEdit,
  getPreview,
  getInstanceNodes,
  getInstances,
  getCollections,
  handleModalClose,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUser();

  const [isLoading, setIsLoading] = useState(false);
  const [instances, setInstances] = useState(null);
  const [instanceNodes, setInstanceNodes] = useState(null);
  const [collections, setCollections] = useState(null);
  const [preview, setPreview] = useState(null);
  const [modal, setModal] = useState(false);

  const toggleModal = () => setModal(!modal);
  const toProfilePage = () => navigate('/profile');

  const [selectedDate, setSelectedDate] = useState(
    moment(data?.periodTime || DEFAULT_BACKUP_TIME, 'HH:mm')
  );

  // validation
  const schema = object({
    subject: string()
      .max(50, t('maxLength', { ns: 'validations', max: '50' }))
      .required(
        sentenceCase(
          t('inputRequired', {
            ns: 'validations',
            field: t('backupSubject', { ns: 'fields' }),
          })
        )
      ),
    instance: mixed().test(
      'instanceRequired',
      sentenceCase(
        t('selectRequired', {
          ns: 'validations',
          field: t('instance', { ns: 'instance' }),
        })
      ),
      (value) => (data ? true : !!value)
    ),
    subscript: string().when(['instance', 'backupType'], {
      is: (instance, backupType) =>
        isSolrCloud(instance?.type) && backupType === BACKUP_TYPES.FILE,
      then: (schema) =>
        schema.required(
          sentenceCase(
            t('selectRequired', {
              ns: 'validations',
              field: t('instanceNode', { ns: 'instance' }),
            })
          )
        ),
      otherwise: (schema) => schema,
    }),
    backupType: string().required(
      sentenceCase(
        t('selectRequired', {
          ns: 'validations',
          field: t('backupType', { ns: 'fields' }),
        })
      )
    ),
    collectionName: string().when('backupType', {
      is: (backupType) => backupType === BACKUP_TYPES.INDEX && !data,
      then: (schema) =>
        schema.required(
          sentenceCase(
            t('selectRequired', {
              ns: 'validations',
              field: t('collection', { ns: 'instance' }),
            })
          )
        ),
      otherwise: (schema) => schema,
    }),
    includeFiles: string(),
    excludeFiles: string(),
    periodType: string().required(
      sentenceCase(
        t('selectRequired', {
          ns: 'validations',
          field: t('backup.schedule', { ns: 'project' }),
        })
      )
    ),
    periodTime: string().required(
      sentenceCase(
        t('selectRequired', {
          ns: 'validations',
          field: t('timePeriod', { ns: 'fields' }),
        })
      )
    ),
    day: string().when('periodType', {
      is: PERIOD_TYPES.WEEK,
      then: (schema) =>
        schema.required(
          sentenceCase(
            t('selectRequired', {
              ns: 'validations',
              field: t('day', { ns: 'fields' }),
            })
          )
        ),
      otherwise: (schema) => schema,
    }),
    date: string().when('periodType', {
      is: PERIOD_TYPES.MONTH,
      then: (schema) =>
        schema.required(
          sentenceCase(
            t('selectRequired', {
              ns: 'validations',
              field: t('date', { ns: 'fields' }),
            })
          )
        ),
      otherwise: (schema) => schema,
    }),
    updateTimeZone: boolean(),
  });

  const {
    handleSubmit,
    register,
    control,
    setValue,
    watch,
    trigger,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      subject: data?.subject || '',
      backupType: data?.category || '',
      excludeFiles: data ? data.excludeFiles : DEFAULT_EXCLUDE_FILES,
      periodType: data?.periodType || '',
      periodTime: data?.periodTime || DEFAULT_BACKUP_TIME,
      day: data && data.periodType === PERIOD_TYPES.WEEK ? data.periodDay : '',
      date:
        data && data.periodType === PERIOD_TYPES.MONTH ? data.periodDay : '',
      historySize: data?.historySize || 1,
      subscript: data?.instanceSubscript || '',
      updateTimeZone: false,
    },
  });

  const { instance, subscript, backupType, periodType } = watch();

  const requireNode = (type) =>
    isSolrCloud(type) && backupType === BACKUP_TYPES.FILE;

  const getInstanceNodesData = async (instanceId) => {
    try {
      const data = await getInstanceNodes(instanceId);
      setInstanceNodes(data);
    } catch (error) {
      setInstanceNodes(null);
    }
  };

  const getInstancesData = async () => {
    try {
      const data = await getInstances();
      setInstances(data);
    } catch (error) {
      setInstances(null);
    }
  };

  const getCollectionData = async () => {
    setIsLoading(true);
    try {
      const data = await getCollections(instance);
      setCollections(data);
    } catch (error) {
      setCollections(null);
    }
    setIsLoading(false);
  };

  const getPreviewData = async () => {
    setIsLoading(true);
    try {
      const formValues = data
        ? { ...getValues(), instance: { id: data.instanceSetId } }
        : getValues();
      const preview = await getPreview(formValues);
      setPreview(preview);
    } catch (error) {
      setPreview(null);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    getInstancesData();
  }, []);

  useEffect(() => {
    if (!instance) return;

    // reset necessary fields
    setPreview(null);
    [
      'backupType',
      'subscript',
      'collectionName',
      'includeFiles',
      'excludeFiles',
    ].forEach((field) => setValue(field, undefined, { shouldTouch: true }));

    if (isSolrCloud(instance.type)) {
      getInstanceNodesData(instance.id);
    }
  }, [instance]);

  useEffect(() => {
    if (!subscript) return;

    // reset necessary field
    setPreview(null);
  }, [subscript]);

  useEffect(() => {
    if (!backupType) return;

    if (backupType === BACKUP_TYPES.INDEX) {
      ['subscript', 'includeFiles', 'excludeFiles'].forEach((field) =>
        trigger(field)
      );
      getCollectionData();
    } else {
      trigger('collectionName');
    }
  }, [backupType]);

  const onSubmit = async (submitData) => {
    data ? await handleEdit(submitData, data) : await handleCreate(submitData);
    handleModalClose?.();
  };

  const refreshPreview = () => {
    getPreviewData();
  };

  return (
    <>
      <form>
        <Grid container spacing={2} justifyContent="center">
          {/* form field: subject */}
          <Grid size={12}>
            <Controller
              name="subject"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={t('backupSubject', { ns: 'fields' })}
                  error={!!errors.subject}
                  helperText={errors.subject?.message}
                  inputProps={{ 'aria-label': 'subject' }}
                  InputLabelProps={data ? { shrink: true } : {}}
                />
              )}
            />
          </Grid>

          {/* form field: instance */}
          <Grid size={12}>
            {data ? (
              <TextField
                fullWidth
                disabled
                defaultValue={data.instanceSetName}
                label={t('instanceToBackup', { ns: 'fields' })}
                InputLabelProps={{ shrink: !!data }}
                inputProps={{ 'aria-label': 'instance name' }}
              />
            ) : (
              <FormControl fullWidth error={!!errors.instance}>
                <InputLabel>
                  {t('instanceToBackup', { ns: 'fields' })}
                </InputLabel>
                <Select
                  {...register('instance')}
                  defaultValue={''}
                  label={t('instanceToBackup', { ns: 'fields' })}
                  inputProps={{ 'aria-label': 'instance select' }}
                >
                  {instances?.map((item) => (
                    <MenuItem key={item.id} value={item}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.instance?.message}</FormHelperText>
              </FormControl>
            )}
          </Grid>
          {/* form field: backup type */}
          <Grid size={12}>
            {data ? (
              <Text>
                {t(find(BACKUP_LABELS, { value: data.category }).label, {
                  ns: 'menus',
                })}
              </Text>
            ) : (
              <FormControl fullWidth error={!!errors.backupType}>
                <Text>{t('backupType', { ns: 'fields' })}</Text>
                <Controller
                  name="backupType"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup {...field} value={field.value || ''}>
                      <FormHelperText>
                        {errors.backupType?.message}
                      </FormHelperText>
                      {BACKUP_LABELS.map((item) => (
                        <FormControlLabel
                          key={item.value}
                          value={item.value}
                          label={t(item.label, { ns: 'menus' })}
                          control={<Radio disabled={!instance} />}
                        />
                      ))}
                    </RadioGroup>
                  )}
                />
              </FormControl>
            )}
          </Grid>
          {/* form field for index backup: collection name */}
          {backupType === BACKUP_TYPES.INDEX && (
            <Grid size={12}>
              {data ? (
                <TextField
                  fullWidth
                  disabled
                  defaultValue={data.collection}
                  label={t('collectionToBackup', { ns: 'fields' })}
                  InputLabelProps={{ shrink: !!data }}
                  inputProps={{ 'aria-label': 'collection name' }}
                />
              ) : isLoading ? (
                <Loading />
              ) : (
                <FormControl fullWidth error={!!errors.collectionName}>
                  <InputLabel>
                    {t('collectionToBackup', { ns: 'fields' })}
                  </InputLabel>
                  <Controller
                    name="collectionName"
                    control={control}
                    defaultValue={''}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label={t('collectionToBackup', { ns: 'fields' })}
                        inputProps={{ 'aria-label': 'collection select' }}
                      >
                        {collections?.map((collection) => (
                          <MenuItem key={collection} value={collection}>
                            {collection}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  <FormHelperText>
                    {errors.collectionName?.message}
                  </FormHelperText>
                </FormControl>
              )}
            </Grid>
          )}
          {/* form field: instance node */}
          {requireNode(data ? data.instanceSetType : instance?.type) && (
            <Grid size={12}>
              {data ? (
                <TextField
                  fullWidth
                  disabled
                  defaultValue={`${data.instanceSetName}-${data.instanceSubscript}`}
                  label={t('instanceNodeToBackup', { ns: 'fields' })}
                  InputLabelProps={{ shrink: !!data }}
                  inputProps={{ 'aria-label': 'instance node' }}
                />
              ) : (
                <FormControl fullWidth error={!!errors.subscript}>
                  <InputLabel>
                    {t('instanceNodeToBackup', { ns: 'fields' })}
                  </InputLabel>
                  <Controller
                    name="subscript"
                    control={control}
                    defaultValue={'all'}
                    render={({ field }) => (
                      <Select
                        {...field}
                        inputProps={{ 'aria-label': 'instance node select' }}
                      >
                        {instanceNodes?.map((node) => (
                          <MenuItem key={node.id} value={node.subscriptNumber}>
                            {node.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  <FormHelperText>{errors.subscript?.message}</FormHelperText>
                </FormControl>
              )}
            </Grid>
          )}
          {/* form field for file backup: included and excluded files */}
          {backupType === BACKUP_TYPES.FILE && (
            <Grid container spacing={3} size={12}>
              <Grid size={6}>
                <Controller
                  name="includeFiles"
                  control={control}
                  defaultValue={
                    data
                      ? data?.includeFiles
                      : instance?.category === INSTANCE_CATEGORY.COMMUNITY
                        ? DEFAULT_COMMUNITY_INCLUDE_FILES
                        : DEFAULT_STANDARD_INCLUDE_FILES
                  }
                  render={({ field }) => (
                    <TextField
                      {...field}
                      minRows={5}
                      label={t('includedFiles', { ns: 'fields' })}
                      variant="outlined"
                      error={!!errors?.includeFiles}
                      helperText={errors?.includeFiles?.message}
                      inputProps={{ 'aria-label': 'include files' }}
                      fullWidth
                      multiline
                    />
                  )}
                />
              </Grid>
              <Grid size={6}>
                <Controller
                  name="excludeFiles"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      minRows={5}
                      label={t('excludedFiles', { ns: 'fields' })}
                      variant="outlined"
                      error={!!errors?.excludeFiles}
                      helperText={errors?.excludeFiles?.message}
                      inputProps={{ 'aria-label': 'exclude files' }}
                      fullWidth
                      multiline
                    />
                  )}
                />
              </Grid>

              {/* file preview */}
              <Grid size={12}>
                <Box sx={{ textAlign: 'right', mb: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    endIcon={<RefreshIcon />}
                    onClick={refreshPreview}
                    aria-label="refresh preview"
                    disabled={
                      isLoading ||
                      (requireNode(
                        data ? data.instanceSetType : instance?.type
                      ) &&
                        !subscript)
                    }
                  >
                    {t('refreshPreviewFile', { ns: 'buttons' })}
                  </Button>
                </Box>
                <Card
                  variant="outlined"
                  aria-label="preview card"
                  style={{ maxHeight: 165, overflow: 'auto' }}
                >
                  <Box sx={{ p: 1 }}>
                    {isLoading ? (
                      <Loading />
                    ) : preview !== null ? (
                      <Text style={{ whiteSpace: 'pre-wrap' }}>
                        {preview || t('noPreviewFiles', { ns: 'descriptions' })}
                      </Text>
                    ) : (
                      <Text caption>
                        {t('showPreviewFiles', { ns: 'descriptions' })}
                      </Text>
                    )}
                  </Box>
                </Card>
              </Grid>
            </Grid>
          )}
        </Grid>

        {/* Schedule */}
        <Box sx={{ mt: 2 }}>
          <Text>{t('backup.schedule', { ns: 'project' })}</Text>
        </Box>
        <Grid container spacing={2}>
          <Grid size={12}>
            <FormControl error={!!errors.periodType}>
              <Controller
                name="periodType"
                control={control}
                render={({ field }) => (
                  <RadioGroup {...field} value={field.value || ''} row>
                    {BACKUP_PERIOD_TYPE_LABELS.map((type) => (
                      <FormControlLabel
                        key={type.value}
                        value={type.value}
                        control={<Radio />}
                        label={t(type.label, { ns: 'menus' })}
                      />
                    ))}
                  </RadioGroup>
                )}
              />
              <FormHelperText>{errors.periodType?.message}</FormHelperText>
            </FormControl>
          </Grid>
          {!handleEdit && (
            <Grid size={12}>
              <Text>
                <Trans
                  i18nKey="userTimeZone"
                  ns="descriptions"
                  components={[<strong key="0" />]}
                  values={{
                    timeZone: user?.timeZone || DEFAULT_TIMEZONE,
                  }}
                />
              </Text>
              <Trans
                i18nKey="changeTimeZone"
                ns="descriptions"
                components={[
                  <Link
                    key="0"
                    component="button"
                    type="button"
                    style={{ paddingBottom: '0.25em' }}
                    variant="body1"
                    onClick={toggleModal}
                    aria-label="user profile"
                    underline="hover"
                  />,
                ]}
              />
            </Grid>
          )}
          <Grid container spacing={5} size={12}>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              {periodType && (
                <Controller
                  name="periodTime"
                  control={control}
                  render={({ field: { onChange }, fieldState: { error } }) => (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <TimePicker
                        ampm={false}
                        minutesStep={10}
                        label={t('timePeriod', { ns: 'fields' })}
                        value={selectedDate}
                        onChange={(date) => {
                          setSelectedDate(date || '');
                          onChange(date ? moment(date).format('HH:mm') : '');
                        }}
                        views={['hours', 'minutes']}
                        slotProps={{
                          textField: {
                            error: !!error,
                            helperText: error ? error.message : null,
                            inputProps: { 'aria-label': 'time-input' },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  )}
                />
              )}
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              {periodType === PERIOD_TYPES.WEEK && (
                <FormControl fullWidth error={!!errors.day}>
                  <InputLabel>{t('day', { ns: 'fields' })}</InputLabel>
                  <Controller
                    name="day"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label={t('day', { ns: 'fields' })}
                        inputProps={{ 'aria-label': 'day-input' }}
                      >
                        {DAYS.map((day) => (
                          <MenuItem key={day.value} value={day.value}>
                            {t(day.label, { ns: 'menus' })}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  <FormHelperText>{errors.day?.message}</FormHelperText>
                </FormControl>
              )}
              {periodType === PERIOD_TYPES.MONTH && (
                <FormControl fullWidth error={!!errors.date}>
                  <InputLabel>{t('date', { ns: 'fields' })}</InputLabel>
                  <Controller
                    name="date"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label={t('date', { ns: 'fields' })}
                        inputProps={{ 'aria-label': 'date-input' }}
                      >
                        {[...Array(31).keys()].map((date) => (
                          <MenuItem key={date} value={date + 1}>
                            {date + 1}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  <FormHelperText>{errors.date?.message}</FormHelperText>
                </FormControl>
              )}
            </Grid>
          </Grid>
          {handleEdit && (
            <Grid size={12}>
              <FormControlLabel
                label={
                  <Trans
                    i18nKey="updateTimeZone"
                    ns="fields"
                    components={[<strong key="0" />]}
                    values={{
                      zone: user?.timeZone,
                    }}
                  />
                }
                control={
                  <Controller
                    name="updateTimeZone"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        {...field}
                        inputProps={{ 'aria-label': 'update timezone' }}
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    )}
                  />
                }
              />
            </Grid>
          )}
        </Grid>

        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
          <Text>{t('numberToKeep', { ns: 'fields' })}: </Text>
          <Box sx={{ minWidth: 100, marginLeft: 16 }}>
            <FormControl fullWidth error={!!errors.historySize}>
              <Controller
                name="historySize"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    inputProps={{ 'aria-label': 'number to keep' }}
                  >
                    {[...Array(50).keys()].map((item) => (
                      <MenuItem key={item} value={item + 1}>
                        {item + 1}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              <FormHelperText>{errors.historySize?.message}</FormHelperText>
            </FormControl>
          </Box>
        </Box>

        <Box sx={{ textAlign: 'center', my: 2 }}>
          <ActionButton type="submit" clickAction={handleSubmit(onSubmit)}>
            {data
              ? t('updateBackup', { ns: 'buttons' })
              : t('createBackup', { ns: 'buttons' })}
          </ActionButton>
        </Box>
      </form>
      <Modal
        openModal={modal}
        title={t('warning', { ns: 'general' })}
        showActions
        handleClose={toggleModal}
        handleConfirm={toProfilePage}
      >
        {t('loseAllData', { ns: 'descriptions' })}
      </Modal>
    </>
  );
};

const isRequired = ({ props, propName, isEdit = false }) => {
  const condition = isEdit
    ? props.data !== undefined
    : props.data === undefined;

  if (condition && typeof props[propName] !== 'function') {
    return new Error(`${propName} function is required`);
  }
};

ScheduledBackupForm.propTypes = {
  data: PropTypes.object,
  handleCreate: (props, propName) => isRequired({ props, propName }),
  getInstances: (props, propName) => isRequired({ props, propName }),
  getInstanceNodes: (props, propName) => isRequired({ props, propName }),
  getCollections: (props, propName) => isRequired({ props, propName }),
  handleEdit: (props, propName) =>
    isRequired({ props, propName, isEdit: true }),
  getPreview: PropTypes.func.isRequired,
  handleModalClose: PropTypes.func,
};

export default ScheduledBackupForm;
